import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { faTag } from '@fortawesome/free-solid-svg-icons';
import { IAiTool, IAiToolSuggestion, IAiToolUseCase } from 'src/app/models/ai-tool.model';
import { EToolCategory, EToolListType } from 'src/app/enum/ai-tool.enum';
import { environment } from 'src/environments/environment';
import { AiToolService } from 'src/app/services/aiTool.service';

@Component({
  selector: 'app-ai-tool-dialog',
  templateUrl: './ai-tool-dialog.component.html',
  styleUrl: './ai-tool-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AiToolDialogComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput: ElementRef;

  @Input() tool?: IAiTool;
  @Input() useCase?: IAiToolUseCase;
  @Input() dialogType = EToolListType.REVIEW;
  @Input() editingMode = false;
  @Input() selectedImage: string = null;

  @Output() onCloseDialog = new EventEmitter<boolean>();
  @Output() onReviewFormSubmit = new EventEmitter<IAiTool>();
  @Output() onSuggestionFormSubmit = new EventEmitter<IAiToolSuggestion>();
  @Output() onUseCaseFormSubmit = new EventEmitter<IAiToolUseCase>();

  public badgeIcon = faTag;
  public newAdvantageControl = new FormControl('', Validators.required);
  public newDisadvantageControl = new FormControl('', Validators.required);
  public toolReviewForm: FormGroup;
  public toolSuggestionForm: FormGroup;
  public toolUseCaseForm: FormGroup;
  public newAdvantage = '';
  public newDisadvantage = '';
  public categories = Object.values(EToolCategory);
  public EToolListType = EToolListType;
  public dragEvent = false;
  public imageLoading = false;
  private objectUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private aiToolService: AiToolService,
  ) {
    this.initForms();
  }

  private initForms(): void {
    this.toolReviewForm = this.fb.group({
      image: [
        '',
        {
          validators: [Validators.required],
          asyncValidators: [this.squareImageValidator()],
          updateOn: 'change',
        },
      ],
      toolName: ['', Validators.required],
      description: ['', Validators.required],
      company: ['', Validators.required],
      website: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+\..+/i)]],
      fileUrl: ['', Validators.pattern(/^https?:\/\/.+\..+/i)],
      advantages: this.fb.array([], this.minOneItemValidator()),
      disadvantages: this.fb.array([], this.minOneItemValidator()),
      conclusion: ['', Validators.required],
      category: ['', Validators.required],
    });

    this.toolSuggestionForm = this.fb.group({
      toolName: ['', Validators.required],
      description: ['', Validators.required],
      website: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+\..+/i)]],
    });

    this.toolUseCaseForm = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      website: ['', [Validators.pattern(/^https?:\/\/.+\..+/i)]],
      fileUrl: ['', Validators.pattern(/^https?:\/\/.+\..+/i)],
    });
  }

  ngOnInit(): void {
    if (this.editingMode && this.tool) {
      if (this.tool.image && typeof this.tool.image === 'string') {
        this.imageLoading = false;
        this.aiToolService.getToolImage(this.tool.image).subscribe({
          next: this.handleImageBlob.bind(this),
          error: (err) => {
            console.error('Error loading image:', err);
            this.imageLoading = true;
          },
        });
      } else if (this.tool.image instanceof File) {
        this.setSelectedImage(this.tool.image as File);
      }

      this.toolReviewForm.patchValue({
        toolName: this.tool.toolName,
        description: this.tool.description,
        company: this.tool.company || '',
        website: this.tool.website,
        fileUrl: this.tool.fileUrl,
        conclusion: this.tool.conclusion,
        category: this.tool.category,
      });

      this.advantages.clear();
      if (this.tool.advantages) {
        this.tool.advantages.forEach((advantage) => {
          this.advantages.push(this.fb.control(advantage, Validators.required));
        });
      }

      this.disadvantages.clear();
      if (this.tool.disadvantages) {
        this.tool.disadvantages.forEach((disadvantage) => {
          this.disadvantages.push(this.fb.control(disadvantage, Validators.required));
        });
      }
    }
  }

  ngOnDestroy(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  public triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.setSelectedImage(file);
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragEvent = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragEvent = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragEvent = false;
    const file = event.dataTransfer?.files[0];
    if (file) this.setSelectedImage(file);
  }

  private squareImageValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Promise<ValidationErrors | null> => {
      const file = control.value;
      if (file && file instanceof File) {
        return new Promise<ValidationErrors | null>((resolve) => {
          const img = new Image();
          const reader = new FileReader();

          reader.onload = () => {
            img.onload = () => {
              if (Math.abs(img.width - img.height) > 2) {
                resolve({ notSquare: true });
              } else {
                resolve(null);
              }
            };
            img.src = reader.result as string;
          };

          reader.readAsDataURL(file);
        });
      }
      return Promise.resolve(null);
    };
  }

  private handleImageBlob(blob: Blob): void {
    // Create object URL and set form values
    this.objectUrl = URL.createObjectURL(blob);
    this.selectedImage = this.objectUrl;
    this.imageLoading = true;

    const file = new File([blob], 'tool-image.png', { type: blob.type });
    this.imageControl.setValue(file);
    this.imageControl.clearValidators();
    this.imageControl.updateValueAndValidity();
  }

  private setSelectedImage(file: File): void {
    // Revoke previous object URL if exists
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }

    this.imageControl.setValue(file);

    // Create object URL instead of using base64
    this.objectUrl = URL.createObjectURL(file);
    this.selectedImage = this.objectUrl;
    this.imageLoading = true;
  }

  get imageControl(): AbstractControl {
    return this.toolReviewForm.get('image');
  }

  get advantages(): FormArray {
    return this.toolReviewForm.get('advantages') as FormArray;
  }

  get disadvantages(): FormArray {
    return this.toolReviewForm.get('disadvantages') as FormArray;
  }

  private minOneItemValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const array = control as FormArray;
      return array && array.length > 0 ? null : { minOneItem: true };
    };
  }

  private addItem(formArray: FormArray, control: FormControl, valueField: string): void {
    if (this[valueField]?.trim()) {
      formArray.push(this.fb.control(this[valueField], Validators.required));
      this[valueField] = '';
    }
  }

  public addAdvantage(): void {
    this.addItem(this.advantages, this.newAdvantageControl, 'newAdvantage');
  }

  public addDisadvantage(): void {
    this.addItem(this.disadvantages, this.newDisadvantageControl, 'newDisadvantage');
  }

  public removeAdvantage(index: number): void {
    this.advantages.removeAt(index);
  }

  public removeDisadvantage(index: number): void {
    this.disadvantages.removeAt(index);
  }

  public onSubmitReview(): void {
    this.toolReviewForm.markAllAsTouched();

    if (!this.toolReviewForm.valid) return;

    const formData = this.toolReviewForm.value;
    if (this.editingMode) {
      formData._id = this.tool._id;

      // Keep image id when editing without changing the image
      if (!formData.image && this.tool.image) {
        formData.image = this.tool.image;
      }
    }
    this.onReviewFormSubmit.emit(formData);
  }

  public onSubmitSuggestion(): void {
    this.toolSuggestionForm.markAllAsTouched();

    if (!this.toolSuggestionForm.valid) return;
    const formData = this.toolSuggestionForm.value;
    this.onSuggestionFormSubmit.emit(formData);
  }

  public onSubmitUseCase(): void {
    this.toolUseCaseForm.markAllAsTouched();

    if (!this.toolUseCaseForm.valid) return;
    const formData = this.toolUseCaseForm.value;
    if (this.editingMode) {
      formData._id = this.useCase._id;
    }

    this.onUseCaseFormSubmit.emit(formData);
  }

  public onKeyUp(event: KeyboardEvent, isAdvantageField = false): void {
    if (event.key === 'Enter') {
      isAdvantageField ? this.addAdvantage() : this.addDisadvantage();
    }
  }

  public onTagSelected(tag: string): void {
    this.toolReviewForm.get('category').setValue(tag);
  }
}
